<template>
    <div class="page watershed">
        <div class="map-view">
            <mapbox-gl-map
                id="map"
                :options="getMapOptions()"
                zoom-control
                @load="onLoad"
                @click="mapClicked"
                @error="mapError"
            />
            <watershed-map-filters @change="updateFilters" />
            <map-locator-scale
                v-if="map"
                :map="map"
            />
            <transition name="fade">
                <map-popup
                    v-if="watershedPopupVisible"
                    id="map-popup-watershed"
                    :title="selectedWatershedName"
                    @close-popup="clearSelectedWatershed"
                >
                    <p>
                        Watershed #{{ selectedWatershedId }}
                    </p>
                    <p class="has-text-centered">
                        <b-button
                            class="is-secondary"
                            :loading="reportLoading"
                            :disabled="reportError"
                            @click="showReport"
                        >
                            {{ reportError ? 'Report Unavailable' : 'Show Report' }}
                        </b-button>
                    </p>
                </map-popup>
            </transition>
            <transition name="fade">
                <map-popup-licence
                    v-if="licencePopupVisible"
                    id="map-popup-licence"
                    :licence-data="selectedLicenceData"
                    @close-popup="clearSelectedLicence"
                />
            </transition>
            <search-bar
                :search-types="searchTypes"
                :loading-results="loadingSearch"
                :search-results="searchResults"
                :geolocate-tooltip-options="geolocateTooltipOptions"
                @select="selectSearchResult"
                @geolocation="goToLocation"
                @input="searchInput"
                @typing="searchBarTyping"
            />
            <style-changer
                v-if="map"
                :selected="mapStyle.label"
                :options="mapStyles"
                @change="changeMapStyle"
            />
            <transition name="fade-fast">
                <activity-indicator
                    v-if="loading"
                    class="has-text-secondary"
                />
            </transition>
        </div>

        <watershed-report
            v-if="selectedWatershedId !== null"
            :key="selectedWatershedId"
            :wfi="selectedWatershedId"
            :watershed-name="selectedWatershedName"
            :fwa="selectedWatershedFwa"
            :lng-lat="clickLngLat"
            @report-data-available="whenReportDataAvailable"
            @report-data-error="whenReportDataError"
        />
    </div>
</template>

<script>
import ActivityIndicator from '@foundryspatial/activity-indicator';
import MapLocatorScale from '@foundryspatial/map-locator-scale';
import MapPopup from '@foundryspatial/map-popup';
import mapStyleSwitcherMixin from '@foundryspatial/map-style-changer/mixin';
import MapboxGlMap from '@foundryspatial/mapbox-gl-map';
import SearchBar from '@foundryspatial/search-bar';

import scssVars from '@/assets/scss/_variables.scss';
import MapPopupLicence from '@/components/MapPopupLicence.vue';
import WatershedMapFilters from '@/components/WatershedMapFilters.vue';
import WatershedReport from '@/components/WatershedReport/WatershedReport.vue';
import { EMPTY_SOURCE_DATA } from '@/constants/geojson.js';
import {
    API_CRITICAL_ERROR,
    API_ERROR_LICENCE_DATA,
    API_ERROR_WATERSHED_DATA,
    NO_DATA_AT_LOCATION,
    SELECTION_OUTSIDE_STUDY_AREA,
} from '@/constants/messages.js';
import {
    GEOLOCATE_TOOLTIP_LABEL,
    SEARCH_TYPE_GEONAMES,
    SEARCH_TYPE_GEONAMES_PLACEHOLDER,
    SEARCH_TYPE_LAT_LONG,
    SEARCH_TYPE_LAT_LONG_PLACEHOLDER,
    SEARCH_TYPE_UTM,
    SEARCH_TYPE_UTM_PLACEHOLDER,
    SEARCH_TYPE_WFI,
    SEARCH_TYPE_WFI_PLACEHOLDER,
} from '@/constants/search.js';
import cursorChanger from '@/mixins/cursorChanger.js';
import searchHelpers from '@/mixins/searchHelpers.js';

export default {
    name: 'Watershed',

    components: {
        SearchBar,
        MapboxGlMap,
        WatershedReport,
        ActivityIndicator,
        WatershedMapFilters,
        MapPopup,
        MapPopupLicence,
        MapLocatorScale,
    },

    mixins: [
        cursorChanger,
        searchHelpers,
        mapStyleSwitcherMixin,
    ],

    data: () => ({
        selectedWatershedId: null,
        selectedWatershedName: null,
        selectedWatershedFwa: null,
        selectedWatershedGeom: null,
        clickLngLat: null,
        reportLoading: false,
        reportError: false,
        gettingWatershed: false,
        watershedPopupVisible: false,
        map: null,
        gettingLicence: false,
        selectedLicenceId: null,
        selectedLicenceData: [],
        licencePopupVisible: false,
        selectedFilters: [],
        searchTypes: [
            {
                label: SEARCH_TYPE_GEONAMES,
                placeholder: SEARCH_TYPE_GEONAMES_PLACEHOLDER,
                icon: 'magnify',
            },
            {
                label: SEARCH_TYPE_LAT_LONG,
                placeholder: SEARCH_TYPE_LAT_LONG_PLACEHOLDER,
                icon: 'map-marker',
            },
            {
                label: SEARCH_TYPE_UTM,
                placeholder: SEARCH_TYPE_UTM_PLACEHOLDER,
                icon: 'map-marker',
            },
            {
                label: SEARCH_TYPE_WFI,
                placeholder: SEARCH_TYPE_WFI_PLACEHOLDER,
                icon: 'map-marker',
            },
        ],

        geolocateTooltipOptions: {
            label: GEOLOCATE_TOOLTIP_LABEL,
            position: 'is-bottom',
            type: 'is-info',
        },

        mapStyles: [
            {
                label: 'Base',
                styleName: process.env.VUE_APP_MAPBOX_DEFAULT_STYLE_NAME,
                styleUrl: process.env.VUE_APP_MAPBOX_DEFAULT_STYLE,
            },
            {
                label: 'Satellite',
                styleName: process.env.VUE_APP_MAPBOX_SATELLITE_STYLE_NAME,
                styleUrl: process.env.VUE_APP_MAPBOX_SATELLITE_STYLE,
            },
        ],
    }),

    computed: {
        // true if any API activity is happening
        loading() {
            return this.map === null || this.gettingWatershed || this.reportLoading || this.gettingLicence;
        },
    },

    methods: {

        /**
         * Handle selected search results
         *
         * @param {object} result - option from the search results.
         */
        selectSearchResult(result) {
            this.clearSelectedFeatures();
            // conciscode is a value from the DB which describes the type of search result
            // in our case we want to handle specific types a bit differently from
            // all the rest. Some examples: `CITY`, `VILG`, `LAKE`, etc.
            // it's sort of like a "category". I dunno where the name comes from
            if (['WATERSHED', 'COORDINATES'].includes(result.conciscode)) {
                // simulate a click on the result's coordinates
                this.getWatershedAtLngLatZoom({ lng: result.lng, lat: result.lat }, 0);
            } else {
                this.goToLocation(result.geojson, result.zoom);
                if (['SW-SHORT', 'GW-SHORT', 'SW-LONG', 'GW-LONG'].includes(result.conciscode)) {
                    // select this licence if we can, too.
                    if ('fs_id' in result.metadata) {
                        this.selectLicence(result.metadata.fs_id);
                    }
                }
            }

            // clear search results
            this.searchResults = [];
        },

        /**
         * Builds and returns a mapbox gl map config object
         *
         * @returns {object} - Mapbox GL map options
         */
        getMapOptions() {
            return {
                style: process.env.VUE_APP_MAPBOX_DEFAULT_STYLE,
                bounds: [[-124.3, 60], [-138.82, 51.69]],
                fitBoundsOptions: {
                    padding: 35,
                },
            };
        },

        /**
         * Add a source and layer if they don't exist in the current style. This is necessary for when we switch a
         * style and need to make sure the source exists in the style. Also check to make sure the source or layer
         * exists before we try to add them here.
         *
         * @param {mapboxgl.Map} map - Mapbox GL map object
         */
        addWatershedSourceIfNeeded(map) {
            // add the watershed-polygon source & layer
            if (!map.getSource('watershed-polygon')) {
                map.addSource('watershed-polygon', {
                    type: 'geojson',
                    data: EMPTY_SOURCE_DATA,
                });
            }
            if (!map.getLayer('watershed-polygon')) {
                map.addLayer({
                    id: 'watershed-polygon',
                    type: 'fill',
                    source: 'watershed-polygon',
                    paint: {
                        'fill-color': scssVars.colorWatershedQuery,
                        'fill-opacity': 0.5,
                        'fill-outline-color': scssVars.colorWatershedQueryBorder,
                    },
                }, process.env.VUE_APP_LICENCE_POINTS_MAP_LAYER);
            }
        },

        /**
         * Add interaction handlers when the map loads
         *
         * @param {MapDataEvent} ev - the MapboxDataEvent which was fired
         * @param {mapboxgl.Map} map - Mapbox GL map object
         */
        onLoad(ev, map) {
            this.map = map;

            // add cursor interaction on map points layer
            this.addCursorChanger(map, [process.env.VUE_APP_LICENCE_POINTS_MAP_LAYER]);
            map.on('click', process.env.VUE_APP_LICENCE_POINTS_MAP_LAYER, this.triggerLicenceClick);

            // add empty source layer for watershed polygon
            this.addWatershedSourceIfNeeded(map);
        },

        /**
         * This is called from the onChange method in the style controller when the map style changes. We want to
         * maintain the selected watershed polygon or licence highlight
         */
        onMapStyleChange() {
            // apply any updated filters
            this.updateFilters(this.selectedFilters);

            // re-add watershed polygon
            if (this.selectedWatershedGeom) {
                this.setWatershedPolygon(this.selectedWatershedGeom);
            }

            // re-add highlight on selected licence
            if (this.selectedLicenceId) {
                this.map.setFilter(process.env.VUE_APP_LICENCE_POINTS_HIGHLIGHT_MAP_LAYER, ['==', 'fs_id', this.selectedLicenceId]);
            }
        },

        /**
         * Get a watershed at a specific point and zoom level
         *
         * @param {object} lngLat - an object containing `lng` and `lat` properties.
         * @param {number} zoom - zoom level of the map - used in searchDistance calculations.
         */
        async getWatershedAtLngLatZoom(lngLat, zoom) {
            // only allow one query at a time
            if (this.loading) return;

            try {
                this.gettingWatershed = true;
                // query for watershed data at the click coordinates
                const watershed = await this.$api.getWatershedByCoords(lngLat, zoom);

                // show a message if no watershed is found at this location
                if (watershed === null) {
                    this.$handleError('', NO_DATA_AT_LOCATION, false, {
                        type: 'is-warning',
                    });
                    return;
                }

                // select watershed feature
                this.selectedWatershedId = watershed.wfi;
                // set selected watershed name
                this.selectedWatershedName = watershed.name;
                this.selectedWatershedFwa = watershed.fwa_code;

                this.clickLngLat = lngLat;
                this.reportLoading = true;
                this.watershedPopupVisible = true;

                // add watershed polygon to the map
                this.setWatershedPolygon(watershed.geom);
            } catch (e) {
                this.reportLoading = false;
                this.$handleError(e, API_ERROR_WATERSHED_DATA);
            } finally {
                this.gettingWatershed = false;
            }
        },

        /**
         * Run when the mapbox gl vue component emits the "click" event
         *
         * @param {MapMouseEvent} ev - the Mapbox GL map click event
         * @param {mapboxgl.Map} map - Mapbox GL map object
         */
        async mapClicked(ev, map) {
            // only allow one query at a time
            if (this.loading) return;

            this.reportError = false;

            // clear selected features
            this.clearSelectedFeatures();

            // find clicked features
            const clickedFeatures = map.queryRenderedFeatures(ev.point);

            // check if the click is outside the study area
            const STUDY_AREA_MASK_ID = 'nwwt-high-res-studyarea-mask-adoona';
            if (clickedFeatures.some((feature) => feature.layer.id === STUDY_AREA_MASK_ID)) {
                // show a message and cancel the click handler
                this.$handleError('', SELECTION_OUTSIDE_STUDY_AREA);
                return;
            }

            // ignore clicks on licence markers
            const markers = map.queryRenderedFeatures(ev.point, {
                layers: [process.env.VUE_APP_LICENCE_POINTS_MAP_LAYER],
            });
            if (markers.length > 0) return;

            await this.getWatershedAtLngLatZoom(ev.lngLat, map.getZoom());
        },

        /**
         * Selects a specific feature in the licence points highlight layer by id
         *
         * @param {string} licenceId - licence id, internally known as the "fs_id".
         */
        async selectLicence(licenceId) {
            this.selectedLicenceId = licenceId;
            // show highlight for this feature by filtering for it
            this.map.setFilter(process.env.VUE_APP_LICENCE_POINTS_HIGHLIGHT_MAP_LAYER, ['==', 'fs_id', licenceId]);

            // query for licence details by fs_id
            try {
                this.gettingLicence = true;
                this.selectedLicenceData = await this.$api.getLicenceDetails(licenceId);
                this.licencePopupVisible = true;
            } catch (e) {
                this.$handleError(e, API_ERROR_LICENCE_DATA);
            } finally {
                this.gettingLicence = false;
            }
        },

        /**
         * Handle clicks on licence markers
         *
         * @param {MapMouseEvent} ev - Mapbox GL map click event
         */
        async triggerLicenceClick(ev) {
            // clear selected features
            this.clearSelectedFeatures();

            // get id from map feature
            const feature = ev.features[0].properties;

            await this.selectLicence(feature.fs_id);
        },

        /**
         * When report data is available, we know the report is done loading
         */
        whenReportDataAvailable() {
            this.reportLoading = false;
        },

        /**
         * Set flags appropriately when there is an error with retrieving report data
         */
        whenReportDataError() {
            this.reportError = true;
            this.reportLoading = false;
            this.gettingWatershed = false;
        },

        /**
         * Scroll the page to show the report element
         */
        showReport() {
            document
                .getElementById('watershed-report')
                .scrollIntoView();
        },

        /**
         * Set the existing watershed-polygon layer source and clears the polygon if null
         *
         * @param {object | null} geometry - polygon geojson (or null to clear)
         */
        setWatershedPolygon(geometry) {
            this.addWatershedSourceIfNeeded(this.map);

            const source = this.map.getSource('watershed-polygon');
            let newData;

            if (geometry === null) {
                // set the source data to an invisible feature
                newData = EMPTY_SOURCE_DATA;
            } else {
                newData = geometry;
                this.goToLocation(newData, 0);
            }

            source.setData(newData);
            this.selectedWatershedGeom = newData; // needed for when we switch between styles.
        },

        /**
         * Unselect any watershed and clear map popup and features
         */
        clearSelectedWatershed() {
            // clear the watershed polygon
            this.setWatershedPolygon(null);

            // unselect watershed
            this.selectedWatershedId = null;
            this.selectedWatershedName = null;
            this.selectedWatershedGeom = null;
            this.clickLngLat = null;
            this.watershedPopupVisible = false;
        },

        /**
         * Unselect any licence and clear map popup
         */
        clearSelectedLicence() {
            // remove all highlights by filtering them out
            this.map.setFilter(process.env.VUE_APP_LICENCE_POINTS_HIGHLIGHT_MAP_LAYER, ['==', 'fs_id', '']);

            this.licencePopupVisible = false;
            this.selectedLicenceId = null;
            this.selectedLicenceData = {};
        },

        /**
         * Unselect any selected map feature(s)
         */
        clearSelectedFeatures() {
            // clear selected watershed, if any
            this.clearSelectedWatershed();

            // clear selected licence, if any
            this.clearSelectedLicence();
        },

        /**
         * Takes a filter expression and updates the map filters
         *
         * @param {Array} val - Mapbox GL filter expression
         */
        updateFilters(val) {
            this.selectedFilters = val;
            if (this.map) {
                this.map.setFilter(process.env.VUE_APP_LICENCE_POINTS_MAP_LAYER, val);
            }
        },

        /**
         * Pass Mapbox GL errors to global error handler to display a message
         *
         * @param {Event} errorEvent - Mapbox GL error event
         */
        mapError(errorEvent) {
            // pass to error handler as a critical error
            this.$handleError(errorEvent, API_CRITICAL_ERROR, true);
        },
    },
};
</script>

<style lang="scss">
</style>
